/**
 * VisibilityResolver — the single cross-workbench visibility contract (A4).
 *
 * Before A2/A4 every workbench decided visibility on its own: the CAD layer
 * checked `entity.visible && layers[entity.layer].visible`, the BIM side
 * checked per-element hidden ids, isolate mode, phases and view filters.
 * That meant "turn this off" behaved differently depending on which
 * workbench you were standing in.
 *
 * This module is the ONE place that turns a SceneObject into
 * `visible / locked` for every consumer (renderers, picking, box-select,
 * the layers panel). Precedence is fixed:
 *
 *   1. object.visible          — the per-object toggle (protocol level)
 *   2. object.locked           — locks are not visibility, but they gate
 *                                interaction everywhere, so they resolve here
 *   3. layer visibility        — shared layer registry (CAD layers and BIM
 *                                layerId entries live in the same map)
 *   4. view scope (BIM-style) — hidden-in-view / temporary isolate
 *
 * Consumers never branch on `object.source`. Workbench-specific extra
 * conditions are passed in as an `extras` predicate by the composing layer,
 * which keeps the core policy free of mode logic.
 */

import type { SceneObject } from '../../core/scene';
import { useAppStore } from '../../state/store';
import { useDocumentStore } from '../../state/useDocumentStore';

/** Minimal structural shape of the shared layer registry. */
export interface LayerState {
    visible: boolean;
    locked: boolean;
}

/** View-scoped state required by the resolver (subset of ViewState). */
export interface ViewScope {
    /** "Hide in View" element ids. */
    hiddenElementIds: string[];
    /** Temporary Hide/Isolate mode. */
    hideIsolateMode: 'isolate' | 'hide' | null;
    /** Category name isolated via the view control bar (null = selection). */
    isolateCategoryName: string | null;
    /** Current selection ids (used by selection-based isolate). */
    selectedIds: string[];
}

export interface VisibilityInput {
    objects: readonly SceneObject[];
    layers: Record<string, LayerState>;
    view: ViewScope;
    /**
     * Workbench-supplied additional condition (e.g. BIM phase filtering,
     * category view styles). Returning false hides the object. The core
     * never interprets `object.source` itself.
     */
    extras?: (obj: SceneObject) => boolean;
}

export interface VisibilityResult {
    visible: boolean;
    locked: boolean;
    /** True when hidden specifically by view-scope rules (for reveal mode). */
    hiddenInView: boolean;
}

/** Resolve one object against the fixed precedence order. */
export function resolveVisibility(
    obj: SceneObject,
    input: Pick<VisibilityInput, 'layers' | 'view' | 'extras'>
): VisibilityResult {
    const layer = input.layers[obj.layerId];
    const layerVisible = layer ? layer.visible : true;
    const layerLocked = layer ? layer.locked : false;

    // 4. View scope — hide-in-view and temporary isolate.
    const view = input.view;
    const hiddenInView = view.hiddenElementIds.includes(obj.id);
    let isolated = true;
    if (view.hideIsolateMode === 'isolate') {
        if (view.isolateCategoryName) {
            isolated = obj.layerId === view.isolateCategoryName;
        } else {
            isolated = view.selectedIds.includes(obj.id);
        }
    } else if (view.hideIsolateMode === 'hide') {
        isolated = !view.selectedIds.includes(obj.id);
    }

    const visible =
        obj.visible &&
        layerVisible &&
        !hiddenInView &&
        isolated &&
        (input.extras ? input.extras(obj) : true);

    return { visible, locked: obj.locked || layerLocked, hiddenInView };
}

/** Batch helper: partition a list into shown / hidden / locked. */
export function partitionByVisibility(
    objects: readonly SceneObject[],
    input: VisibilityInput
): { visible: SceneObject[]; hidden: SceneObject[]; lockedIds: Set<string> } {
    const visible: SceneObject[] = [];
    const hidden: SceneObject[] = [];
    const lockedIds = new Set<string>();

    for (const obj of objects) {
        const r = resolveVisibility(obj, input);
        if (r.locked) lockedIds.add(obj.id);
        if (r.visible) visible.push(obj);
        else hidden.push(obj);
    }
    return { visible, hidden, lockedIds };
}

/**
 * Build the resolver input from live stores: the scene ledger provides the
 * objects, the app store provides layers and view scope. Kept out of React
 * so tools, pickers and overlays can call it from imperative code paths.
 */
export function currentVisibilityInput(): VisibilityInput {
    const app = useAppStore.getState();
    return {
        objects: Object.values(useDocumentStore.getState().sceneObjects),
        layers: app.layers,
        view: {
            hiddenElementIds: app.view.hiddenElementIds,
            hideIsolateMode: app.view.hideIsolateMode,
            isolateCategoryName: app.view.isolateCategoryName,
            selectedIds: app.selectedIds,
        },
    };
}
